<template>
  <div v-if="currentAgency && conversationStatus" class="conversation-info rounded">
    <div class="d-flex flex-column px-3 py-2 gap-2 border-bottom">
      <AssignConversationButton
        v-if="
          currentConversation.status == 'active' &&
          agencySettings.enableConversationAssignment === true &&
          currentConversation.operator_id === null &&
          $permission('assign:conversation')
        "
        :conversationId="currentConversation.conversation_id"
      />
      <UnassignConversationButton
        v-if="
          currentConversation.status == 'active' &&
          agencySettings.enableConversationAssignment === true &&
          (currentConversation.operator_id === currentUser.user_id || (currentConversation.operator_id !== null && $permission('admin:agency')))
        "
        :conversationId="currentConversation.conversation_id"
      />
      <ReopenConversationButton
        v-if="
          currentConversation.status == 'closed' &&
          agencySettings.enableConversationAssignment === true &&
          (currentConversation.operator_id === currentUser.user_id || (currentConversation.operator_id !== null && $permission('admin:agency')))
        "
        :conversationId="currentConversation.conversation_id"
      />
      <CloseConversationButton
        v-if="generalConditionForCTA"
        :conversationId="currentConversation.conversation_id"
      />
      <TransferConversationButton
        v-if="currentConversation.tcc_session && generalConditionForCTA"
        :conversationId="currentConversation.conversation_id"
        :sessionId="currentConversation.tcc_session.session_id"
      />
    </div>
    <div class="px-3 py-2">
      <div class="row pb-1">
        <div class="col-4 pt-1 text-nowrap">
          <h6>Initiated</h6>
        </div>
        <div class="col-8 ">
          <span class="text-nowrap">
            {{
              formatTimestamp(currentConversation.last_message_at || currentConversation.created_at, "MMM D, YYYY h:mm a")
            }}
          </span>
          <div v-if="currentConversation.status == 'active'" class="font-italic text-muted" style="font-size:70%;">
            {{ formatTimestamp(currentConversation.created_at, 'fromnow') }}
          </div>
        </div>
      </div>
      <div class="row pb-1">
        <div class="col-4 pt-1 text-nowrap">
          <h6>Agent</h6>
        </div>
        <div class="col-8  text-nowrap">
          <span v-if="currentConversation.status == 'active' && currentConversation.operator_id === currentUser.user_id"
            title="Reassign Agent" style="cursor: pointer" @click="openChangeAgentModal()">
            <u>{{ currentConversation.operator_name }}</u>
          </span>
          <span v-else>{{ currentConversation.operator_name }}</span>
        </div>
      </div>
      <div class="row pb-1">
        <div class="col-4 pt-1 text-nowrap">
          <h6>Language</h6>
        </div>
        <div class="col-8 text-nowrap">
          <span
            v-if="currentConversation.channel != 'media' && currentConversation.status == 'active' && currentConversation.operator_id === currentUser.user_id"
            title="Update Recipient Language" style="cursor: pointer" @click="openChangeLangModal()">
            <u>{{ recipientLanguage }}</u>
          </span>
          <span v-else>
            {{ recipientLanguage !== 'Set Language' ? recipientLanguage : '' }}
          </span>
        </div>
      </div>
      <div v-if="currentConversation.channel != 'media'" class="row">
        <div class="col-4 pt-1 text-nowrap">
          <h6>Number</h6>
        </div>
        <div class="col-8 mt-1 text-truncate">
          <span class="text-nowrap">
            {{ prettyPhoneNumber(currentConversation.number) }}
          </span>
        </div>
      </div>
      <div v-if="currentConversation.channel != 'media'" class="row">
        <div class="col-4 pt-1 text-nowrap">
          <h6 class="d-flex align-items-center">
            Participants ({{ currentConversation.recipients.length }})
            <b-icon-plus-circle
              v-if="
                currentConversation.status === 'active' &&
                currentConversation.channel === 'voice' &&
                currentConversation.provider_number.capabilities.includes('speech')
              "
              class="add-recipient"
              @click="openAddRecipientModal"
            />
          </h6>
        </div>
        <div class="col-8 mt-1 text-truncate">
          <div
            v-for="recipient in currentConversation.recipients"
            :key="recipient.recipient_id"
            class="text-nowrap"
            :title="recipient.label"
          >
            {{ prettyPhoneNumber(recipient.ten_dlc || recipient.number) }}
          </div>
        </div>
      </div>
      <div class="row" v-if="currentConversation.channel == 'media'">
        <div class="col-4 pt-1 text-nowrap">
          <h6>Media File</h6>
        </div>
        <div class="col-8 text-truncate">
          <span :title="currentConversation.resource_file">
            <a :href="currentConversation.resource_file" target="_blank">
              {{ splitFilename(currentConversation.resource_file) }}
            </a>
          </span>
        </div>
      </div>
      <div class="row" v-if="currentConversation.channel == 'media'">
        <div class="col-4 pt-1 text-nowrap">
          <h6>Participants</h6>
        </div>
        <div v-if="currentConversation.recipients && currentConversation.recipients.length > 0" class="col-8">
          {{ currentConversation.recipients.length }}
        </div>
      </div>
    </div>
    <div v-if="recordedAudioAttachments.length > 0" class="conversation-info--audio px-3 py-2 border-top d-flex flex-wrap gap-2">
      <MediaItem
        v-for="attachment in recordedAudioAttachments"
        recordedAudio
        :key="attachment.attachment_id"
        :attachmentId="attachment.attachment_id"
        :attachment="attachment"
        :processed="attachment.status === 'processed'"
      />
    </div>
    <div v-if="keywords.length > 0"
      class="conversation-info--keywords px-3 py-2 border-top d-flex flex-wrap gap-2">
      <div
        v-for="keyword in keywords"
        :key="`${keyword.id}-${keyword.term}`"
        :class="`keyword-item text-nowrap d-flex justify-content-center align-items-center fs-3 px-2 py-1 urgency-level-${keyword.data ? keyword.data.level : 1}`"
      >
        {{ keyword.term }}
      </div>
    </div>
    <div v-show="hasSummary" class="conversation-info--summary px-3 py-2 border-top">
      <div :id="`conversationSummary-${conversationId}`"></div>
    </div>
    <ConversationActions :conversationId="conversationId" :currentRecipientLang="currentRecipientLang" />
  </div>
</template>

<script>
/* eslint-disable */
import { EventBus } from "../../events/eventBus";
import { mapGetters } from "vuex";
import ConversationActions from "./ConversationActions";
import CloseConversationButton from "../action_buttons/CloseConversationButton";
import ReopenConversationButton from "../action_buttons/ReopenConversationButton.vue";
import AssignConversationButton from "../action_buttons/AssignConversationButton";
import UnassignConversationButton from "../action_buttons/UnassignConversationButton";
import TransferConversationButton from "../action_buttons/TransferConversationButton";
import RecipientLanguageForm from "./RecipientLanguageForm";
import ConversationAgentForm from "./ConversationAgentForm";
import AddRecipientForm from "./AddRecipientForm.vue";
import formatter from "../../mixins/formatter";
import MediaItem from "@/components/MediaItem";

export default {
  name: "ConversationInfo",
  mixins: [formatter],
  props: {
    currentRecipientLang: String,
    conversationId: String,
  },
  components: {
    CloseConversationButton,
    AssignConversationButton,
    UnassignConversationButton,
    TransferConversationButton,
    ReopenConversationButton,
    ConversationActions,
    MediaItem
  },
  data() {
    return {
      keywordIds: [],
      hasSummary: false,
      summary: '',
    };
  },
  computed: {
    ...mapGetters("user", ["currentToken"]),
    ...mapGetters("agency", ["currentAgency", "textDialects", "agencySettings"]),
    ...mapGetters("conversations", ["currentConversation", "conversationStatus", "conversationAttachments"]),
    ...mapGetters("user", ["currentUser"]),
    ...mapGetters("keywords", ["keywords"]),
    internationalDialCodeEnabled() {
      return this.agencySettings.enableInternational == "true";
    },
    recipientLanguage() {
      if (this.currentConversation.channel === "media") {
        let langLabels = new Set();
        this.currentConversation.recipients.forEach((r) => {
          if (r.language && r.language !== "")
            langLabels.add(this.dialectLabel(r.language));
        })
        return Array.from(langLabels).join(", ");
      }
      else
        return this.dialectLabel(this.currentRecipientLang)
    },
    generalConditionForCTA() {
      const { status, operator_id } = this.currentConversation;
      const { enableConversationAssignment } = this.agencySettings;

      if (status === 'active') {
        if (enableConversationAssignment === false) {
          return true;
        }
        if (enableConversationAssignment === true && (operator_id === this.currentUser.user_id || (operator_id !== null && this.$permission('admin:agency')))) {
          return true;
        }
      }
      return false;
    },
    recordedAudioAttachments() {
      return this.conversationAttachments.filter(attachment => attachment.content_type.toLowerCase() === 'audio/pcm');
    },
  },
  created() {
    this.keywordIds = [];
    this.hasSummary = false;
    this.summary = '';
    EventBus.$on("update-conversation-keyword", (data) => {
      const { isNew, data: keyword, conversationId } = data;
      if (conversationId === this.conversationId) {
        if (isNew) {
          this.$toastr(
            "info",
            `Key term "<b>${keyword.term}</b>" triggered`,
            'Keyword Detected',
          );
        }
      }
    });
    EventBus.$on("update-conversation-summary", (data) => {
      const { isNew, data: summary, conversationId } = data;
      if (conversationId === this.conversationId && summary) {
        this.hasSummary = true;
      }
    });
  },
  methods: {
    dialectLabel(code) {
      if (!!code) {
        const langCode = code.toLowerCase();

        let languageLabel = langCode;
        try {
          languageLabel = this.textDialects.find(o => o.code === langCode).label;
        } catch (error) {
          return langCode;
        }

        return languageLabel;
      } else {
        return "Set Language";
      }
    },
    openChangeLangModal() {
      const $ = window.$;
      $("#id-modal-container").modal({
        backdrop: "static"
      });
      EventBus.$emit("open", {
        component: RecipientLanguageForm,
        title: "Change Recipient Language",
        closeOnClick: true,
        props: {
          language: this.currentRecipientLang
        }
      });
    },
    openChangeAgentModal() {
      const $ = window.$;
      $("#id-modal-container").modal({
        backdrop: "static"
      });
      EventBus.$emit("open", {
        component: ConversationAgentForm,
        title: "Reassign Agent",
        closeOnClick: true,
        props: {
          conversationId: this.currentConversation.conversation_id,
          operatorId: this.currentConversation.operator_id
        }
      });
    },
    openAddRecipientModal() {
      const $ = window.$;
      $("#id-modal-container").modal({
        backdrop: "static"
      });
      EventBus.$emit("open", {
        component: AddRecipientForm,
        title: "Add Recipient",
        closeOnClick: true,
        props: {}
      });
    }
  },
};
</script>

<style lang="scss" scoped>
.conversation-info {
  width: 100%;
  max-width: 1024px;

  &--summary {
    min-height: 2.25rem;
  }

  &--keywords {
    height: auto;
    min-height: 2.25rem;
    max-height: 300px;
    overflow-y: auto;

    .keyword-item {
      width: fit-content;
      height: 1.25rem;
      border-radius: 1rem;
      color: white;
      background-color: #186dde;

      &.urgency-level-1 {
        background-color: #ef5350;
      }

      &.urgency-level-2 {
        background-color: #ffb22b;
      }
    }
  }

  .add-recipient {
    cursor: pointer;
    color: #186dde;
    font-size: 1.125rem;
    font-weight: bold;
    margin-left: 0.5rem;
    :hover {
      color: #0d6efd;
    }
  }
}
</style>
